import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Container, 
  TextField,
  Button,
  Paper,
  Stack,
  Link,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  MenuItem,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Zoom,
  Grow,
  Fade
} from '@mui/material';
import { 
  Phone,
  WhatsApp,
  Email,
  AccessTime,
  Place,
  Facebook,
  Instagram,
  Send,
  Schedule,
  Directions,
  LocationOn
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Replace the JustDialIcon component with this simpler version
const JustDialIcon = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '14px',
      fontFamily: "'Poppins', sans-serif",
    }}
  >
    JD
  </Box>
);

const FadeInSection = ({ children, direction = 'up', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = React.useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => setIsVisible(entry.isIntersecting));
    });
    
    observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);

  const getTransform = () => {
    switch (direction) {
      case 'left':
        return 'translateX(-50px)';
      case 'right':
        return 'translateX(50px)';
      case 'up':
        return 'translateY(50px)';
      case 'down':
        return 'translateY(-50px)';
      default:
        return 'translateY(50px)';
    }
  };

  return (
    <Box
      ref={domRef}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'none' : getTransform(),
        transition: `all 0.9s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
        height: '100%'
      }}
    >
      {children}
    </Box>
  );
};

const ContactUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [contactInfo, setContactInfo] = useState(JSON.parse(localStorage.getItem('contactInfo') || JSON.stringify({
    address: 'Begur - Hulimavu Rd, Raghavendra Layout, Akshayanagara West, Krishna Layout, Akshayanagar, Bengaluru, Karnataka 560076',
    mapLink: 'https://www.google.com/maps/place/Careflex+physiotherapy+(Homecare+physio)/@12.8739635,77.6139061,17z/data=!3m1!4b1!4m6!3m5!1s0x3bae6b0027e775d5:0xa49ed9408152cd22!8m2!3d12.8739635!4f77.6139061!16s%2Fg%2F11ww3d8_yp?entry=ttu',
    phone: '+91 7619678243',
    email: 'info@careflexphysiotherapy.com',
    hours: 'Mon - Sun: 7:00 AM - 9:00 PM'
  })));
  const [locations, setLocations] = useState(JSON.parse(localStorage.getItem('locations') || '[]'));
  const [socialLinks, setSocialLinks] = useState(JSON.parse(localStorage.getItem('socialLinks') || JSON.stringify({
    facebook: 'https://www.facebook.com/careflexphysiotherapy',
    instagram: 'https://www.instagram.com/careflexphysiotherapy',
    twitter: 'https://twitter.com/careflexphysio',
    linkedin: 'https://www.linkedin.com/company/careflexphysiotherapy',
    youtube: 'https://www.youtube.com/@careflexphysiotherapy',
    justdial: 'https://www.justdial.com/Bangalore/Careflex-Physiotherapy/080PXX80-XX80-250123152727-K2P3_BZDET'
  })));

  useEffect(() => {
    const handleStorageChange = () => {
      setContactInfo(JSON.parse(localStorage.getItem('contactInfo') || JSON.stringify(contactInfo)));
      setLocations(JSON.parse(localStorage.getItem('locations') || '[]'));
      setSocialLinks(JSON.parse(localStorage.getItem('socialLinks') || JSON.stringify(socialLinks)));
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    setShowSuccess(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactMethods = [
    {
      icon: <Phone />,
      title: 'Phone Call',
      description: 'Call us directly for immediate assistance',
      action: `tel:${contactInfo.phone}`,
      actionText: 'Call Now',
      color: '#4CAF50',
    },
    {
      icon: <WhatsApp />,
      title: 'WhatsApp',
      description: 'Chat with us on WhatsApp',
      action: `https://wa.me/${contactInfo.phone.replace(/\D/g, '')}`,
      actionText: 'Start Chat',
      color: '#25D366',
    },
    {
      icon: <Email />,
      title: 'Email',
      description: 'Send us an email',
      action: `mailto:${contactInfo.email}`,
      actionText: 'Send Email',
      color: '#EA4335',
    },
  ];

  const socialMediaLinks = [
    {
      icon: Facebook,
      color: '#1877F2',
      label: 'Facebook',
      link: socialLinks.facebook
    },
    {
      icon: Instagram,
      color: '#E4405F',
      label: 'Instagram',
      link: socialLinks.instagram
    },
    {
      icon: WhatsApp,
      color: '#25D366',
      label: 'WhatsApp',
      link: `https://wa.me/${contactInfo.phone.replace(/[^0-9]/g, '')}`
    },
    {
      icon: JustDialIcon,
      color: '#FF0000',
      label: 'JustDial',
      link: socialLinks.justdial
    }
  ];

  return (
    <Box sx={{ py: 8, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
          {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
                  sx={{ 
              fontWeight: 700,
                    mb: 2,
              background: 'linear-gradient(45deg, #4CAF50, #45a049)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
                  }}
                >
            Contact Us
                </Typography>
                <Typography 
                  variant="h6" 
                  color="text.secondary" 
            sx={{ maxWidth: '600px', mx: 'auto' }}
          >
            Get in touch with us for any questions about our physiotherapy services or to schedule an appointment.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Contact Information Cards */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {contactMethods.map((method, index) => (
                <Card
                  key={index}
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          bgcolor: `${method.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                        }}
                      >
                        {React.cloneElement(method.icon, {
                          sx: { color: method.color, fontSize: 28 },
                })}
              </Box>
                      <Typography variant="h6" component="h3">
                        {method.title}
                      </Typography>
                    </Box>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      {method.description}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={method.icon}
                      href={method.action}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: method.color,
                        '&:hover': {
                          bgcolor: method.color,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {method.actionText}
                    </Button>
                      </CardContent>
                    </Card>
              ))}
            </Stack>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
              <Paper
              elevation={3}
                sx={{
                p: 4,
                  height: '100%',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
                  Send us a Message
                </Typography>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                      onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                      size="large"
                        startIcon={<Send />}
                        sx={{
                        bgcolor: '#4CAF50',
                          '&:hover': {
                          bgcolor: '#45a049',
                          },
                        }}
                      >
                      Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
          </Grid>

          {/* Location Information */}
          <Grid item xs={12}>
              <Paper
              elevation={3}
                sx={{
                  p: 4,
                  mt: 4,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
                  Our Locations
                </Typography>
              <Grid container spacing={3}>
                {locations.map((location, index) => (
                  <Grid item xs={12} md={6} key={location.id}>
                    <Card
                      sx={{
                        height: '100%',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                        sx={{ 
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              bgcolor: '#4CAF5020',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                            }}
                          >
                            <LocationOn sx={{ color: '#4CAF50', fontSize: 28 }} />
                          </Box>
                          <Typography variant="h6" component="h3">
                            {location.name}
                            {location.branch && ` (${location.branch})`}
                      </Typography>
                        </Box>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                          {location.fullAddress}
                      </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          startIcon={<Phone />}
                          href={`tel:${location.phone}`}
                            sx={{ color: '#4CAF50', borderColor: '#4CAF50' }}
                          >
                            {location.phone}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<Directions />}
                            href={location.mapLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.fullAddress)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: '#4CAF50', borderColor: '#4CAF50' }}
                          >
                            Get Directions
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Social Media Section */}
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                mt: 4,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
                Connect With Us
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                {socialMediaLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <Tooltip key={index} title={social.label}>
                      <IconButton
                        component="a"
                        href={social.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                          bgcolor: 'white',
                          color: social.color,
                          width: 56,
                          height: 56,
                          transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                            bgcolor: social.color,
                            color: 'white',
                            transform: 'translateY(-5px)',
                            boxShadow: `0 5px 15px ${social.color}40`,
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: '1.75rem' }} />
                      </IconButton>
                    </Tooltip>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Thank you for your message! We'll get back to you soon.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactUs; 